import { useEffect, useRef, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import ApiClient from 'api/axiosClient';
import billingApiClient from 'api/billingApiClient';
import appointment from 'assets/tickets/appointment.svg';
import Button from 'common-ui/button';
import DragDropUploadFile from 'common-ui/dragDropUploadFile';
import Input from 'common-ui/input';
import { AsyncSelect, TypeToSearchSelect } from 'common-ui/select';
import { LoadContainer, LoadingIcon } from 'common-ui/table/Table.styles';
import loadIcon from 'common-ui/table/loading.svg';
import TextEditor from 'common-ui/textEditor';
import {
  TICKET_APPOINTMENT_DURATION_OPTIONS,
  TICKET_APPOINTMENT_REMINDER_OPTIONS,
  TICKET_APPOINTMENT_WEEKDAY_OPTIONS,
} from 'constants/constants';
import {
  createAppointment,
  editAppointment,
  getAppointmentContext,
  getSingleAppointment,
} from 'features/projectManagement/ProjectManagementActions';
import {
  clearSingleAppointment,
  resetSuccess,
  selectLoading,
  selectSingleAppointment,
  selectSuccess,
} from 'features/projectManagement/ProjectManagementSlice';
import { createFormData, generateOptions, stripHtmlTags } from 'utils';

import {
  BtnWrapper,
  Container,
  Form,
  Icon,
  LeftContent,
  RightContent,
  Title,
  Wrapper,
} from '../createEditTicket/CreateEditTicket.styles';
import { AttachmentText } from '../singleTicketView/SingleTicketView.styles';
import Appointment from './appointment';
import { schemaWithAppointment } from './schema';

const getDurationView = (d) => {
  const result = TICKET_APPOINTMENT_DURATION_OPTIONS.find(
    (duration) => duration.value === String(d)
  );

  return result ?? null;
};

const getReminderView = (d) => {
  const result = TICKET_APPOINTMENT_REMINDER_OPTIONS.find(
    (reminder) => reminder.value === String(d)
  );

  return result ?? null;
};

const convertWeekdaysToObject = (weekdaysArray) => {
  return weekdaysArray.reduce((acc, { weekday, time }) => {
    acc[`appt-time-${weekday}`] = { startTime: time };
    return acc;
  }, {});
};

const CreateEditAppointments = () => {
  const location = useLocation();
  const projectUuid = location.state?.projectUuid;
  const crmUser = location.state?.crmUser;
  const lead = location.state?.lead;
  const [projectOptions, setProjectOptions] = useState([]);
  const [subprojectOptions, setSubprojectOptions] = useState([]);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [newOffers, setNewOffers] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [subprojectId, setSubprojectId] = useState(null);
  const [deletedFileIds, setDeletedFileIds] = useState([]);

  const appointmentData = useSelector(selectSingleAppointment);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const defaultCustomerOption =
    crmUser?.id || crmUser?.phone
      ? {
          label: `${crmUser.id || crmUser.phone} - ${crmUser.name}`,
          value: crmUser.id || crmUser.phone,
        }
      : null;
  const isLoading = useSelector(selectLoading);
  const success = useSelector(selectSuccess);
  // const fileUploadRef = useRef(null);

  const dispatch = useDispatch();

  const { uuid } = useParams();
  const navigate = useNavigate();
  const isFormInitialized = useRef(false);
  const lastAppointmentId = useRef(null);
  const hasManuallyChangedCustomer = useRef(false);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    unregister,
    formState: { errors },
    clearErrors,
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(schemaWithAppointment),
    defaultValues: {
      'appt-description': '',
      project: null,
      customerId: crmUser
        ? {
            label: `${crmUser.id} - ${crmUser.name}`,
            value: crmUser.id,
          }
        : null,
      subprojectId: null,
      assignee: null,
      comment: '',
      files: [],
      watchers: null,
      'appt-frequency': { label: 'One-time', value: 'One-time' },
      'appt-location': '',
      'appt-weekdays': [],
      communication: {
        'phone-call': { enabled: true, value: '' },
        'text-message': { enabled: false, value: '' },
        email: { enabled: false, value: '' },
      },
      'appt-reminder': TICKET_APPOINTMENT_REMINDER_OPTIONS[0],
      currentOffers:
        crmUser?.offers && Array.isArray(crmUser.offers)
          ? crmUser.offers.map((offer) => offer.tariffName).join(', ')
          : '',
    },
  });

  const onSubmit = (data) => {
    const { customerId, subprojectId, assignee, files, watchers, project } = data;

    // Build weekdays array for Weekly frequency
    const buildWeekdays = () => {
      if (data['appt-frequency']?.value !== 'Weekly') return undefined;

      return Object.keys(data)
        .filter((key) => key.startsWith('appt-time-'))
        .map((key) => {
          const weekdayNumber = parseInt(key.split('-')[2], 10);
          return {
            time: data[key]?.startTime,
            weekday: weekdayNumber,
          };
        });
    };

    if (uuid) {
      const customerIdValue = customerId?.value || appointmentData?.customerId;

      // Build edit params (same structure as create)
      const editParams = {
        ...(lead
          ? { leadUuid: lead.id, leadInfo: lead.name }
          : customerIdValue
            ? { customerId: customerIdValue }
            : {}),
        appointmentId: uuid,
        title: appointmentData?.title || '',
        description: data['appt-description'] || '',
        location: data['appt-location'] || '',
        serviceId: data['appt-service']?.value || '',
        subServiceId: data['appt-sub-service']?.value || null,
        reasonId: data['appt-reason']?.value || null,
        frequency: data['appt-frequency']?.value || '',
        duration: data['appt-duration']?.value ? Number(data['appt-duration'].value) : null,
        reminder: data['appt-reminder']?.value ? data['appt-reminder'].value : null,
        timezoneId: data['appt-timezoneId']?.value || null,
        disabled: data['appt-disabled'] ?? false,
        assigneeIds: assignee?.filter((item) => !item.isTeam).map((item) => item.value) ?? [],
        teamIds: assignee?.filter((item) => item.isTeam).map((item) => item.value) ?? [],
        watcherIds: watchers?.map((item) => item.value) ?? [],
        currentOffer: data.currentOffers || '',
        communicationMethod: {
          email: data.communication?.email?.value || '',
          phoneCall: data.communication?.['phone-call']?.value || '',
          textMessage: data.communication?.['text-message']?.value || '',
        },
      };

      // Only add new offer fields if they have values
      if (data.selectedOfferId?.value) {
        editParams.selectedOfferId = data.selectedOfferId.value;
      }
      if (data.newOffers) {
        editParams.newOffers = data.newOffers;
      }

      if (files && files.length > 0) {
        editParams.files = files;
      }

      if (deletedFileIds?.length > 0) {
        editParams.removedFileIds = deletedFileIds;
      }

      const frequency = data['appt-frequency']?.value;
      if (frequency === 'One-time' || frequency === 'Monthly') {
        editParams.date = data['appt-date'] || null;
      } else if (frequency === 'Daily') {
        editParams.time = data['appt-time']?.startTime || null;
      } else if (frequency === 'Weekly') {
        editParams.weekdays = buildWeekdays();
      }
      if (uuid) {
        if (stripHtmlTags(data.comment).trim().length > 0) {
          editParams.comment = data.comment ?? '';
        }
      }

      const formData = new FormData();
      const formDataParams = createFormData(formData, editParams);
      dispatch(editAppointment(formDataParams));
      return;
    }

    const params = {};
    if (files) {
      params.files = files;
    }
    params.projectId = project?.value;
    if (lead) {
      params.leadUuid = lead.id;
      params.leadInfo = lead.name;
      params.orderId = lead.orderId;
      params.leadNumber = lead.leadNumber;
    } else if (customerId?.value) {
      params.customerId = customerId.value;
    }
    params.subprojectId = subprojectId?.value;
    params.assigneeIds =
      assignee?.filter((item) => item.isTeam === false).map((item) => item.value) ?? [];
    params.teamIds =
      assignee?.filter((item) => item.isTeam === true).map((item) => item.value) ?? [];
    params.watcherIds = watchers?.map((item) => item.value) ?? [];
    params.currentOffer = lead?.currentOffer ?? data?.currentOffers;
    params.newOffers = data.newOffers;
    params.serviceId = data['appt-service']?.value || null;

    params.subServiceId = data['appt-sub-service']?.value || null;
    params.reasonId = data['appt-reason']?.value || null;
    params.appointment = {
      location: data['appt-location'],
      description: data['appt-description'] || '',
      reminder: data['appt-reminder']?.value,
      frequency: data['appt-frequency']?.value,
      duration: data['appt-duration']?.value,
      service: data['appt-service']?.value,
      timezoneId: data['appt-timezoneId']?.value,
      communicationMethod: {
        email: data.communication?.email?.value || '',
        phoneCall: data.communication?.['phone-call']?.value || '',
        textMessage: data.communication?.['text-message']?.value || '',
      },
    };

    if (
      data['appt-frequency']?.value === 'One-time' ||
      data['appt-frequency']?.value === 'Monthly'
    ) {
      params.appointment = {
        ...params.appointment,
        date: data['appt-date'],
      };
    } else if (data['appt-frequency']?.value === 'Daily') {
      params.appointment = {
        ...params.appointment,
        time: data['appt-time']?.startTime,
      };
    } else if (data['appt-frequency'].value === 'Weekly') {
      params.appointment = {
        ...params.appointment,
        weekdays: buildWeekdays(),
      };
    }

    // Only add new offer fields if they have values
    if (data.selectedOfferId?.value) {
      params.selectedOfferId = data.selectedOfferId.value;
    }
    if (data.newOffers) {
      params.newOffers = data.newOffers;
    }

    const formData = new FormData();
    const formDataParams = createFormData(formData, params);
    dispatch(createAppointment(formDataParams));
  };

  const handleExitCreateEdit = (e) => {
    e?.preventDefault();
    dispatch(resetSuccess());
    navigate(-1);
  };

  const removeFile = (fileId) => {
    setDeletedFileIds((prev) => [...prev, fileId]);
    setUploadedFiles((prev) => prev.filter((file) => file.uuid !== fileId));
  };

  const handleProjectChange = (selectedProject) => {
    setProjectId(selectedProject);
    setValue('project', selectedProject);
    setValue('subprojectId', null);
    setValue('assignee', null);
    setValue('watchers', null);
    setSubprojectOptions([]);
  };

  const handleSubprojectChange = (selectedSubproject) => {
    setSubprojectId(selectedSubproject);
    setValue('subprojectId', selectedSubproject);
    setValue('assignee', null);
    setValue('watchers', null);
  };

  const fetchSubprojectOptions = async (searchTerm) => {
    try {
      if (!projectId?.value && !projectId?.uuid) {
        return [];
      }

      const projectUuidValue = projectId?.value || projectId?.uuid;
      const url = searchTerm
        ? `/projects/subprojects/${projectUuidValue}?name=${encodeURIComponent(searchTerm)}`
        : `/projects/subprojects/${projectUuidValue}`;

      const response = await ApiClient.get(url);
      const options = generateOptions(response.subprojects || [], null, 'uuid', 'name', [
        'subprojectType',
      ]);
      setSubprojectOptions(options);
      return options;
    } catch (error) {
      console.error('Error fetching subproject options:', error);
      return [];
    }
  };

  const fetchAssigneeOptions = async (searchTerm) => {
    try {
      const url = searchTerm
        ? `/tickets/subproject-members-teams/${subprojectId?.value || subprojectId?.uuid}?name=${encodeURIComponent(searchTerm)}`
        : `/tickets/subproject-members-teams/${subprojectId?.value || subprojectId?.uuid}`;

      const response = await ApiClient.get(url);
      const options = generateOptions(response.members || []);
      setAssigneeOptions(options);
      return options;
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchWatchersOptions = async (searchTerm) => {
    try {
      const url = searchTerm
        ? `/users/subproject-members/${subprojectId?.value || subprojectId?.uuid}?name=${encodeURIComponent(searchTerm)}`
        : `/users/subproject-members/${subprojectId?.value || subprojectId?.uuid}`;

      const response = await ApiClient.get(url);
      const options = generateOptions(response.users || []);
      setAssigneeOptions(options);
      return options;
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchNewOfferOptions = async (searchTerm) => {
    try {
      const url = searchTerm
        ? `/tariff/active?name=${encodeURIComponent(searchTerm)}`
        : `/tariff/active`;

      const response = await billingApiClient.get(url);
      const options = generateOptions(response.data);
      setNewOffers(options);
      return options;
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchCustomerOptions = async (searchTerm) => {
    try {
      const trimmedTerm = searchTerm?.trim();
      if (!trimmedTerm || trimmedTerm.length < 1) {
        return [];
      }

      const response = await billingApiClient.get(
        `/sales/customer-exists?id=${encodeURIComponent(trimmedTerm)}`
      );

      const customer = response.data;
      if (!customer) return [];

      return [
        {
          label: `${customer.customerId} - ${customer.fullName}`,
          value: customer.customerId,
        },
      ];
    } catch (error) {
      console.error('Error fetching customer options:', error);
      return [];
    }
  };

  const fetchAllProjectOptions = async (searchTerm) => {
    try {
      const url = searchTerm
        ? `/projects/user/all?name=${encodeURIComponent(searchTerm)}`
        : `/projects/user/all`;

      const response = await ApiClient.get(url);
      const options = generateOptions(response.projects || [], null, 'uuid', 'name', [
        'subprojects',
      ]);
      setProjectOptions(options);
      return options;
    } catch (error) {
      console.error('Error fetching project options:', error);
      return [];
    }
  };

  useEffect(() => {
    if (uuid) {
      dispatch(getSingleAppointment(uuid));
    } else {
      dispatch(clearSingleAppointment());
      isFormInitialized.current = false;
      lastAppointmentId.current = null;
    }

    if (crmUser) {
      const customerId = crmUser?.id || crmUser?.phone;

      if (customerId) {
        dispatch(getAppointmentContext(customerId));
      }
    }

    fetchAllProjectOptions();
  }, []);

  useEffect(() => {
    if (success.edit || success.create) {
      dispatch(resetSuccess());
      navigate(-1);
    }
  }, [success.edit, success.create, dispatch, navigate]);

  useEffect(() => {
    if (!uuid || !appointmentData?.uuid) {
      return;
    }

    isFormInitialized.current = true;
    lastAppointmentId.current = appointmentData.uuid;

    if (appointmentData.customerId) {
      dispatch(getAppointmentContext(appointmentData.customerId));
    }

    setSubprojectId(appointmentData.subproject);

    const projectData = {
      label: appointmentData.project?.name,
      value: appointmentData.project?.uuid,
      ...appointmentData.project,
    };
    setProjectId(projectData);

    if (appointmentData.project?.uuid) {
      ApiClient.get(`/projects/subprojects/${appointmentData.project.uuid}`)
        .then((response) => {
          const options = generateOptions(response.subprojects || [], null, 'uuid', 'name', [
            'subprojectType',
          ]);
          setSubprojectOptions(options);
        })
        .catch((error) => {
          console.error('Error fetching subprojects in edit mode:', error);
        });
    }

    if (appointmentData.files) {
      setValue('files', appointmentData.files);
      setUploadedFiles(appointmentData.files);
    }

    const weekdaysObject = appointmentData?.weekdays
      ? convertWeekdaysToObject(appointmentData.weekdays)
      : {};

    const localMappedWeekdays = appointmentData?.weekdays
      ? appointmentData.weekdays
          .map(({ weekday, time }) => {
            const dayOption = TICKET_APPOINTMENT_WEEKDAY_OPTIONS.find(
              (opt) => opt.value == weekday
            );
            return dayOption ? { ...dayOption, time } : null;
          })
          .filter(Boolean)
      : [];
    const formData = {
      project: projectData,
      customerId: appointmentData.customerId
        ? { label: appointmentData.customerId, value: appointmentData.customerId }
        : null,
      subprojectId: {
        label: appointmentData.subproject?.name,
        value: appointmentData.subproject?.uuid,
      },
      assignee: generateOptions([
        ...(appointmentData.assignees || []),
        ...(appointmentData.assignedTeams || []),
      ]),
      watchers: generateOptions(appointmentData.watchers || []),
      'appt-location': appointmentData?.location,
      'appt-description': appointmentData?.description ?? '',
      'appt-timezoneId': {
        label: appointmentData?.timezone?.name,
        value: appointmentData?.timezone?.uuid,
      },

      'appt-service': appointmentData?.service
        ? { label: appointmentData.service, value: appointmentData?.serviceEntity?.uuid }
        : null,
      'appt-sub-service': appointmentData?.subService
        ? { label: appointmentData.subService.name, value: appointmentData.subService.uuid }
        : null,
      'appt-reason': appointmentData?.reason
        ? { label: appointmentData.reason.name, value: appointmentData.reason.uuid }
        : null,
      'appt-frequency': {
        label: appointmentData?.frequency,
        value: appointmentData?.frequency,
      },
      'appt-weekdays': localMappedWeekdays,
      'appt-duration': getDurationView(appointmentData?.duration),
      'appt-reminder': getReminderView(appointmentData?.reminder ?? 'null'),
      communication: {
        'phone-call': {
          enabled: !!appointmentData?.communicationMethod?.phoneCall,
          value: appointmentData?.communicationMethod?.phoneCall || '',
        },
        'text-message': {
          enabled: !!appointmentData?.communicationMethod?.textMessage,
          value: appointmentData?.communicationMethod?.textMessage || '',
        },
        email: {
          enabled: !!appointmentData?.communicationMethod?.email,
          value: appointmentData?.communicationMethod?.email || '',
        },
      },
      'appt-date': appointmentData?.date,
      ...weekdaysObject,
      'appt-disabled': appointmentData?.disabled,
      'appt-time': { startTime: appointmentData?.time },
      currentOffers: appointmentData?.currentOffer || '',
      selectedOfferId:
        appointmentData?.selectedOffer != null && appointmentData?.selectedOffer !== ''
          ? { label: appointmentData?.selectedOffer, value: appointmentData?.selectedOffer }
          : null,
    };
    reset(formData);
  }, [appointmentData, uuid, reset, dispatch, setValue]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  if (isLoading.singleTicket)
    return (
      <LoadContainer>
        <LoadingIcon src={loadIcon} alt="Loading..." />
      </LoadContainer>
    );

  return (
    <Container>
      <Title>{uuid ? 'Edit' : 'Create'} appointment</Title>
      <Form
        onSubmit={handleSubmit(onSubmit, (errors) =>
          console.log('Validation errors:', errors)
        )}
        onKeyDown={handleKeyDown}
      >
        <Wrapper>
          <LeftContent>
            {lead || appointmentData?.leadUuid ? (
              <>
                {lead && (
                  <Input
                    label="Lead"
                    value={`${lead.leadNumber} - ${lead.name}${lead.phone ? ` - ${lead.phone}` : ''}`}
                    disabled
                  />
                )}
                {(lead?.id || appointmentData?.leadUuid) && uuid && (
                  <Input
                    label="Lead"
                    value={
                      appointmentData?.leadNumber
                        ? appointmentData?.leadNumber + ' - ' + appointmentData?.leadInfo
                        : appointmentData?.leadUuid + ' - ' + appointmentData?.leadInfo
                    }
                    disabled
                  />
                )}
              </>
            ) : (
              <Controller
                name="customerId"
                control={control}
                render={({ field, fieldState }) => (
                  <TypeToSearchSelect
                    {...field}
                    label="Customer ID"
                    $error={fieldState.error?.message}
                    placeholder="Type to search customer..."
                    inputType="number"
                    loadOptions={fetchCustomerOptions}
                    defaultOptions={defaultCustomerOption ? [defaultCustomerOption] : []}
                    debounceDelay={500}
                    isClearable
                    value={field.value}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption);
                      hasManuallyChangedCustomer.current = true;
                      if (selectedOption?.value) {
                        dispatch(getAppointmentContext(selectedOption.value));
                      } else {
                        setValue('currentOffers', '');
                      }
                    }}
                  />
                )}
              />
            )}
            <Controller
              name="project"
              control={control}
              render={({ field, fieldState }) => (
                <AsyncSelect
                  {...field}
                  label="Project"
                  placeholder="Select project"
                  req
                  isDisabled={!!uuid}
                  $error={fieldState.error?.message}
                  loadOptions={fetchAllProjectOptions}
                  onMenuOpen={fetchAllProjectOptions}
                  defaultOptions={projectOptions}
                  onChange={(selectedProject) => handleProjectChange(selectedProject)}
                />
              )}
            />

            <Controller
              name="subprojectId"
              control={control}
              render={({ field, fieldState }) => (
                <AsyncSelect
                  {...field}
                  label="Subproject / Activity"
                  placeholder="Assign activity"
                  req
                  isDisabled={(!projectId && !projectUuid) || !!uuid}
                  $error={fieldState.error?.message}
                  loadOptions={fetchSubprojectOptions}
                  onMenuOpen={fetchSubprojectOptions}
                  defaultOptions={subprojectOptions}
                  onChange={(selectedSubproject) => handleSubprojectChange(selectedSubproject)}
                />
              )}
            />
            <Controller
              name="assignee"
              control={control}
              render={({ field, fieldState }) => (
                <AsyncSelect
                  {...field}
                  isMulti
                  req
                  placeholder="Assign members"
                  label="Assignee"
                  menuPlacement="top"
                  isDisabled={!subprojectId}
                  $error={fieldState.error?.message}
                  loadOptions={fetchAssigneeOptions}
                  onMenuOpen={fetchAssigneeOptions}
                  defaultOptions={assigneeOptions}
                />
              )}
            />
            <Controller
              name="watchers"
              control={control}
              render={({ field }) => (
                <AsyncSelect
                  {...field}
                  isMulti
                  label="Watchers"
                  placeholder="Assign watchers"
                  isDisabled={!subprojectId}
                  loadOptions={fetchWatchersOptions}
                  onMenuOpen={fetchWatchersOptions}
                  defaultOptions={assigneeOptions}
                />
              )}
            />
          </LeftContent>
          <RightContent>
            {watch('customerId')?.value && (
              <Controller
                name="currentOffers"
                control={control}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    label="Current offer"
                    disabled={true}
                    error={fieldState.error?.message}
                    maxLength={250}
                    placeholder="No current offer"
                  />
                )}
              />
            )}
            {watch('customerId')?.value && (
              <Controller
                name="selectedOfferId"
                control={control}
                render={({ field }) => (
                  <AsyncSelect
                    {...field}
                    label="New offer"
                    defaultOptions={newOffers}
                    loadOptions={fetchNewOfferOptions}
                    onMenuOpen={fetchNewOfferOptions}
                    onInputChange={fetchNewOfferOptions}
                    placeholder="Select offer"
                    isClearable
                    isSearchable
                  />
                )}
              />
            )}
            <Controller
              name="files"
              control={control}
              render={({ field }) => (
                <>
                  <DragDropUploadFile
                    {...field}
                    onFilesChange={(files) => setValue('files', files)}
                    removeDefaultFile={removeFile}
                    defaultFiles={uploadedFiles}
                  />
                </>
              )}
            />
          </RightContent>
        </Wrapper>
        {uuid && (
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <TextEditor
                {...field}
                label="Add notes"
                placeholder="Start writing here..."
                onChange={(newComment) => setValue('comment', newComment)}
              />
            )}
          />
        )}
        {uuid && (
          <AttachmentText
            style={{
              marginTop: '18px',
            }}
          >
            <Icon src={appointment} alt="appointment" />
            Appointment
          </AttachmentText>
        )}
        <Appointment
          control={control}
          setValue={setValue}
          watch={watch}
          unregister={unregister}
          errors={errors}
          clearErrors={clearErrors}
          hasLead={!!(lead?.id || appointmentData?.leadUuid)}
        />
        <BtnWrapper>
          <Button
            outlined
            type="button"
            onClick={handleExitCreateEdit}
            disabled={isLoading.edit || isLoading.create}
          >
            Cancel
          </Button>
          <Button secondary type="submit" loading={isLoading.edit || isLoading.create}>
            {uuid ? 'Save appointment' : 'Create appointment'}
          </Button>
        </BtnWrapper>
      </Form>
    </Container>
  );
};

export default CreateEditAppointments;
